import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, Cake, GlobeHemisphereWest, IdentificationCard, Camera, LockKey } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import Avatar from "@/components/Avatar";

const ROLE_LABEL = { admin: "Administrateur", professeur: "Professeur", etudiant: "Étudiant" };

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2.5 last:border-0">
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon size={15} />} {label}
      </span>
      <span className="text-sm font-medium">{value || "—"}</span>
    </div>
  );
}

export default function MonCompte() {
  const { user, refreshUser } = useAuth();
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  if (!user) return null;

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/users/${user.id}/avatar`, fd);
      await refreshUser();
      toast.success("Photo de profil mise à jour");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAvatarUploading(false); }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword) return toast.error("Complétez tous les champs.");
    if (newPassword !== confirmPassword) return toast.error("Les deux mots de passe ne correspondent pas.");
    if (newPassword.length < 6) return toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères.");
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Mot de passe modifié");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPassword(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Mon compte</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Mon profil</h1>
        <p className="text-sm text-muted-foreground">Consultez vos informations, changez votre photo et votre mot de passe.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar user={user} size={72} />
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold">{user.civilite ? `${user.civilite} ` : ""}{user.name}</p>
            <Badge variant="secondary" className="mt-1">{ROLE_LABEL[user.role]}</Badge>
          </div>
        </div>
        <div className="mt-4">
          <Label className="flex items-center gap-1.5 text-sm"><Camera size={15} /> Changer ma photo de profil</Label>
          <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/*" disabled={avatarUploading} onChange={(e) => uploadAvatar(e.target.files[0])} className="mt-1" data-testid="my-avatar-input" />
          {avatarUploading && <p className="mt-1 text-xs text-muted-foreground">Envoi en cours...</p>}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-2 font-display text-base font-bold">Mes informations</h2>
        <p className="mb-3 text-xs text-muted-foreground">Pour corriger une information, contactez l'administration.</p>
        <InfoRow icon={IdentificationCard} label="Identifiant" value={user.username} />
        <InfoRow icon={EnvelopeSimple} label="Email" value={user.email} />
        <InfoRow icon={Phone} label="Téléphone" value={user.phone} />
        <InfoRow icon={Cake} label="Date de naissance" value={user.birth_date} />
        <InfoRow icon={GlobeHemisphereWest} label="Nationalité" value={user.nationality} />
        {user.role === "etudiant" && (
          <>
            <InfoRow label="Numéro d'étudiant" value={user.student_number} />
            <InfoRow label="Filière" value={user.filiere} />
            <InfoRow label="Promotion" value={user.promotion} />
            <InfoRow label="Semestre" value={user.semestre} />
          </>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-3 flex items-center gap-1.5 font-display text-base font-bold"><LockKey size={18} /> Changer mon mot de passe</h2>
        <div className="space-y-3">
          <div><Label>Mot de passe actuel</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="current-password" /></div>
          <div><Label>Nouveau mot de passe</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password" /></div>
          <div><Label>Confirmer le nouveau mot de passe</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password" /></div>
          <Button onClick={changePassword} disabled={savingPassword} className="bg-primary hover:bg-primary/90" data-testid="save-password-btn">
            {savingPassword ? "Enregistrement..." : "Modifier le mot de passe"}
          </Button>
        </div>
      </div>
    </div>
  );
}
